import React, { useState, useCallback, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
} from "@mui/material";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams } from "react-router-dom";
import { getMyLabWithAttendanceCode, learnerCheckIn, learnerCheckOut } from "../../services/AttendanceServices";
import ErrorScreen from "../ErrorScreen";
import AttendanceModal from "./AttendanceModal";
import ConfirmCheckInOut from "../ConfirmCheckInOut";
import AttendanceHistory from "./AttendanceHistory";

const MarkAttendance = () => {
  const [loadingMark, setLoadingMark] = useState(false);
  const [isCheckIn, setIsCheckIn] = useState(false)
  const [showModal, setShowModal] = useState(false);
  const [labData, setLabData] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmActionType, setConfirmActionType] = useState("");
  const [loading, setLoading] = useState(true);

  const { attendanceCode } = useParams();
  const attendancePath=JSON.parse(localStorage.getItem("attendancePath"))
  const handleViewAttendance = useCallback(() => {
    setShowModal(true);
  }, []);
  const today = new Date().toLocaleDateString("en-CA"); 
  const fetchLabDetails = async () => {
    setLoading(true);
    try {
      let response;
      if (attendanceCode && attendancePath?.storedAt===today) {
        response = await getMyLabWithAttendanceCode(attendanceCode);
        setLabData(response.data.data);
      }
      // else {
      //   response = await getMyLabWithoutAttendanceCode();
      //   setLabData(response.data.data);
      // }
    } catch (error) {
      const message = error.response?.data?.message || error.response.message || error.message || "Something went wrong. Please try again later.";
      if (message === "Lab details not found for this learner, please consult with mentor for lab allocation!") {
        setErrorMessage(message);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabDetails();
  }, [attendanceCode]);

  const requiredLocation = {
    lat: labData?.labAttendance?.coe.latitude,
    lng: labData?.labAttendance?.coe.longitude,
  };

  // const fetchCurrentLocation = (actionType) => {
  //   if (navigator.geolocation) {
  //     navigator.geolocation.getCurrentPosition(
  //       (position) => {
  //         const { latitude, longitude } = position.coords;
  //         if (labData?.labAttendance?.geo_fencing_allowed) {
  //           const distance = calculateDistance(
  //             latitude,
  //             longitude,
  //             requiredLocation?.lat,
  //             requiredLocation?.lng
  //           );
  //           if (distance > labData?.labAttendance?.coe.geo_fencing_distance / 1000) {
  //             toast.error(`You are outside the allowed range. Distance: ${distance.toFixed(2)} km`);
  //           } else {
  //             if (actionType === "checkin") {
  //               console.log("with geofencing", latitude, longitude)
  //               handleCheckIn(latitude, longitude);
  //             } else {
  //               handleCheckOut(latitude, longitude);
  //             }
  //           }
  //         } else {
  //           if (actionType === "checkin") {
  //             console.log("without geofencing", latitude, longitude)
  //             handleCheckIn(latitude, longitude);
  //           } else {
  //             handleCheckOut(latitude, longitude);
  //           }
  //         }
  //       },
  //       () => toast.error("Unable to fetch location. Please allow location access.")
  //     );
  //   } else {
  //     toast.error("Geolocation is not supported by this browser.");
  //   }
  // };
  const fetchCurrentLocation = (actionType) => {
    if (labData?.labAttendance?.geo_fencing_allowed) {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by this browser.");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const distance = calculateDistance(
            latitude,
            longitude,
            requiredLocation?.lat,
            requiredLocation?.lng
          );
          if (distance > labData?.labAttendance?.coe.geo_fencing_distance / 1000) {
            toast.error(`You are outside the allowed range. Distance: ${distance.toFixed(2)} km`);
          } else {
            handleLocationAction(actionType, latitude, longitude);
          }
        },
        (error) => {
          // console.error("Geolocation error:", error);       
          switch (error.code) {
            case error.PERMISSION_DENIED:
              toast.error("Location permission denied. Please enable location access in browser settings.");
              break;
            case error.POSITION_UNAVAILABLE:
              toast.error("Location information is unavailable. Check your device's location services.");
              break;
            case error.TIMEOUT:
              toast.error("Location request timed out. Please try again.");
              break;
            default:
              toast.error("Unable to fetch location. An unknown error occurred.");
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      handleLocationAction(actionType);
    }
  };

  const handleLocationAction = (actionType, latitude = null, longitude = null) => {
    if (actionType === "checkin") {
      // console.log("Checking in with location:", { latitude, longitude });
      handleCheckIn(latitude, longitude);
    } else {
      // console.log("Checking out with location:", { latitude, longitude });
      handleCheckOut(latitude, longitude);
    }
  };
  const openConfirmModal = (actionType) => {
    setConfirmActionType(actionType);
    setConfirmOpen(true);
  };

  const confirmCheckInOut = () => {
    setConfirmOpen(false);
    fetchCurrentLocation(confirmActionType);
  };


  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const toRadians = (degree) => (degree * Math.PI) / 180;
    const φ1 = toRadians(lat1);
    const φ2 = toRadians(lat2);
    const Δφ = toRadians(lat2 - lat1);
    const Δλ = toRadians(lon2 - lon1);
    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const handleCheckIn = async (latitude = null, longitude = null) => {
    if (isCheckIn) return;
    setIsCheckIn(true);
    const obj = latitude && longitude ? { latitude, longitude } : { latitude: 0, longitude: 0 };
    try {
      const response = await learnerCheckIn(obj, labData?.labAttendance?.attendanceCode);
      toast.success(response?.data?.message);
      fetchLabDetails();
    } catch (error) {
      toast.error(error.response?.data?.message);
    } finally {
      setTimeout(() => {
        setIsCheckIn(false);
      }, 3000);
    }
  };

  const handleCheckOut = async (latitude = null, longitude = null) => {
    if (loadingMark) return;
    const obj = latitude && longitude ? { latitude, longitude } : { latitude: 0, longitude: 0 };
    try {
      setLoadingMark(true);
      const response = await learnerCheckOut(obj);
      toast.success(response?.data?.message);
      fetchLabDetails();
    } catch (error) {
      toast.error(error.response?.data?.message);
    } finally {
      setTimeout(() => {
        setLoadingMark(false);
      }, 1000);
    }
  };

  const getGreeting = () => {
    const name = JSON.parse(localStorage.getItem("user")).learner?.name;
    const hour = new Date().getHours();
    if (hour < 12) return `Good Morning , ${name}`;
    if (hour < 16) return `Good Afternoon, ${name}`;
    return `Good Evening, ${name}`;
  };
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={100} />
      </Box>
    );
  }
  return (
    <>
    {(attendanceCode && attendancePath?.storedAt===today)?
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            flexWrap: "wrap",
            mb: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            color="primary"
            sx={{
              fontSize: { xs: "18px", sm: "20px", md: "24px" },
            }}
          >
            {getGreeting()}
          </Typography>
          {(!errorMessage) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleViewAttendance}
              sx={{
                ml: { xs: 0, sm: 0, md: 2 },
                mt: { xs: 3, sm: 0, md: 0 },
              }}
            >
              View Attendance
            </Button>
          )}
        </Box>
        {errorMessage ? (
          <ErrorScreen message={errorMessage} />
        ) : (
          <Box
            sx={{
              width: "90%",
              maxWidth: 550,
              p: 3,
              borderRadius: 3,
              boxShadow: 5,
              bgcolor: "background.paper",
              position: "relative"
            }}
          >
            {(
              (!labData?.labAttendance?.isCheckoutEnabled && labData?.todaysAttendance?.inDateTime) ||
              labData?.todaysAttendance?.outDateTime
            ) && (
                <Box sx={{ position: "absolute", top: 10, right: 10 }}>
                  <CheckCircleRoundedIcon style={{ color: "green" }} />
                </Box>
              )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" fontWeight="bold">
                  Mentor:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{labData?.labAttendance?.user.displayName || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" fontWeight="bold">
                  Lab Name:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{labData?.labAttendance?.lab.lab_name || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" fontWeight="bold">
                  Lab Timing:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>
                  {labData?.labAttendance?.lab.startTime ? `${labData?.labAttendance?.lab.startTime} to ${labData?.labAttendance?.lab.endTime}` : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1" fontWeight="bold">
                  Current Date and Time:
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{new Date().toLocaleString()}</Typography>
              </Grid>
              {labData?.todaysAttendance && <>
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" fontWeight="bold">
                      CheckIn Date and Time:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography>
                      {labData?.todaysAttendance?.inDateTime
                        ? new Date(labData?.todaysAttendance?.inDateTime).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                        : 'Loading...'}
                    </Typography>
                  </Grid>
                </>  {labData?.todaysAttendance?.outDateTime && <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" fontWeight="bold">
                      CheckOut Date and Time:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography>
                      {labData?.todaysAttendance?.outDateTime
                        ? new Date(labData?.todaysAttendance.outDateTime).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                        : 'Loading...'}
                    </Typography>
                  </Grid>
                </>}
                {(labData?.todaysAttendance?.dailyPracticeTime >= 0 && labData?.todaysAttendance?.dailyPracticeTime != null) && <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1" fontWeight="bold">
                      Total Practice Hours:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography>
                      {labData?.todaysAttendance?.dailyPracticeTime || "-"}
                    </Typography>
                  </Grid>
                </>}
              </>}
              <Grid item xs={12} display="flex" justifyContent="center">
                {(labData?.labAttendance?.isCheckoutEnabled && labData?.todaysAttendance?.inDateTime && !labData?.todaysAttendance?.outDateTime) ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => openConfirmModal("checkout")}
                    disabled={loadingMark}
                    sx={{ py: 1, px: 3 }}
                  >
                    {loadingMark ? <CircularProgress size={24} color="inherit" /> : "Check Out"}
                  </Button>
                ) : (
                  !labData?.todaysAttendance?.inDateTime &&
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => openConfirmModal("checkin")}
                    disabled={isCheckIn || !labData}
                    sx={{ py: 1, px: 3 }}
                  >
                    {isCheckIn ? <CircularProgress size={24} color="inherit" /> : "Check In"}
                  </Button>

                )}
              </Grid>
            </Grid>
          </Box>
        )}
        <AttendanceModal open={showModal} onClose={() => setShowModal(false)} />
        <ConfirmCheckInOut
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={confirmCheckInOut}
          actionType={confirmActionType} />
      </Box>
      : <AttendanceHistory/>
      
    }
    </>
  );
};

export default MarkAttendance;
